import type { LucideIcon } from 'lucide-react';
import {
  Cpu,
  CreditCard,
  HardDrive,
  Image,
  Key,
  Plus,
  Receipt,
  Server,
  Settings,
} from 'lucide-react';

export interface AppMenuItem {
  icon?: LucideIcon;
  isActive: boolean;
  title: string;
  url: string;
}

export interface AppMenuGroup {
  items: AppMenuItem[];
  title: string;
}

export interface AppBreadcrumbItem {
  href?: string;
  isCurrentPage?: boolean;
  label: string;
}

interface AppMenuItemDef {
  icon: LucideIcon;
  matches: (pathname: string) => boolean;
  title: string;
  url: string;
}

interface AppMenuGroupDef {
  items: AppMenuItemDef[];
  title: string;
}

const routeLabels: Record<string, string> = {
  '/market': 'GPU 市场',
  '/create': '创建实例',
  '/instances': '我的实例',
  '/instances/:id': '实例详情',
  '/images': '我的镜像',
  '/storage': '数据存储',
  '/billing/balance': '财务中心',
  '/billing/records': '费用分析',
  '/settings': 'SSH 密钥',
  '/admin': '运营中心',
};

const menuDefinitions: AppMenuGroupDef[] = [
  {
    title: 'GPU 市场',
    items: [
      {
        title: 'GPU 市场',
        url: '/market',
        icon: Cpu,
        matches: (p) => p === '/market' || p === '/',
      },
      {
        title: '创建实例',
        url: '/create',
        icon: Plus,
        matches: (p) => p === '/create',
      },
    ],
  },
  {
    title: '我的资源',
    items: [
      {
        title: '我的实例',
        url: '/instances',
        icon: Server,
        matches: (p) => p === '/instances' || p.startsWith('/instances/'),
      },
      {
        title: '我的镜像',
        url: '/images',
        icon: Image,
        matches: (p) => p === '/images',
      },
      {
        title: '数据存储',
        url: '/storage',
        icon: HardDrive,
        matches: (p) => p === '/storage',
      },
    ],
  },
  {
    title: '费用管理',
    items: [
      {
        title: '财务中心',
        url: '/billing/balance',
        icon: CreditCard,
        matches: (p) => p === '/billing/balance',
      },
      {
        title: '费用分析',
        url: '/billing/records',
        icon: Receipt,
        matches: (p) => p === '/billing/records',
      },
    ],
  },
  {
    title: '设置',
    items: [
      {
        title: 'SSH 密钥',
        url: '/settings',
        icon: Key,
        matches: (p) => p === '/settings',
      },
    ],
  },
];

const adminMenuGroup: AppMenuGroupDef = {
  title: '管理',
  items: [
    {
      title: '运营中心',
      url: '/admin',
      icon: Settings,
      matches: (p) => p === '/admin',
    },
  ],
};

export function buildMenuItems(
  pathname: string,
  options?: { isAdmin?: boolean },
): AppMenuGroup[] {
  const defs = options?.isAdmin
    ? [...menuDefinitions, adminMenuGroup]
    : menuDefinitions;
  return defs.map((group) => ({
    title: group.title,
    items: group.items.map((item) => ({
      icon: item.icon,
      isActive: item.matches(pathname),
      title: item.title,
      url: item.url,
    })),
  }));
}

function matchRoutePattern(pathname: string): string | undefined {
  if (routeLabels[pathname]) return pathname;
  for (const pattern of Object.keys(routeLabels)) {
    if (!pattern.includes(':')) continue;
    const regex = new RegExp(`^${pattern.replace(/:[^/]+/g, '[^/]+')}$`);
    if (regex.test(pathname)) return pattern;
  }
  return undefined;
}

export function buildBreadcrumbs(pathname: string): AppBreadcrumbItem[] {
  const matched = matchRoutePattern(pathname);
  if (!matched) return [{ label: 'GPU 市场', href: '/market' }];
  return [
    {
      label: routeLabels[matched] ?? pathname,
      href: matched.includes(':') ? undefined : matched,
      isCurrentPage: true,
    },
  ];
}
